import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'

import * as TanStackQueryProvider from './integrations/tanstack-query/root-provider.tsx'
// Import the generated route tree
import { routeTree } from './routeTree.gen'
import './styles.css'
import reportWebVitals from './reportWebVitals.ts'

import { ErrorBoundary } from '@/components/error-boundary/error-boundary'
import { MaintenancePage } from '@/components/maintenance/maintenance-page'
import { AppUpdateNotifier } from '@/components/pwa/app-update-notifier'
import { PwaShell } from '@/components/pwa/pwa-shell'
import { capturePwaInstallPromptEarly } from '@/lib/pwa-utils'
import { hasAccessToken, initializeTokenStorage } from '@/lib/token-storage'
import {
  applyAuthHandoffFromUrlIfNeeded,
  restoreHandoffTokenFromSessionStorage,
} from '@/lib/auth-handoff-bootstrap'
import { refreshToken } from '@/api/auth/auth.actions'

// Captura beforeinstallprompt antes do React montar (evento único do Chrome)
capturePwaInstallPromptEarly()

if (typeof window !== 'undefined') {
  type ClarityFn = ((...args: Array<unknown>) => void) & { q: Array<unknown> }
  const win = window as Window & { clarity?: ClarityFn }
  if (!win.clarity) {
    const q: Array<unknown> = []
    const fn = ((...args: Array<unknown>) => {
      q.push(args)
    }) as ClarityFn
    fn.q = q
    win.clarity = fn
  }
  const t = document.createElement('script')
  t.async = true
  t.src = 'https://www.clarity.ms/tag/py77uz98wq'
  const first = document.getElementsByTagName('script')[0]
  first?.parentNode?.insertBefore(t, first)
}

restoreHandoffTokenFromSessionStorage()
await initializeTokenStorage()

if (await applyAuthHandoffFromUrlIfNeeded()) {
  // Navegação completa para /workspace (ou destino seguro) em andamento.
} else if (!hasAccessToken()) {
  try {
    await refreshToken()
  } catch {
    // sem cookie/sessão ativa: segue fluxo normal de autenticação
  }
}

// Create a new router instance
const TanStackQueryProviderContext = TanStackQueryProvider.getContext()
const router = createRouter({
  routeTree,
  context: {
    ...TanStackQueryProviderContext,
  },
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const MAINTENANCE_MODE = import.meta.env.VITE_MAINTENANCE_MODE === 'true'

// Render the app
const rootElement = document.getElementById('app')
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <ErrorBoundary>
        <TanStackQueryProvider.Provider {...TanStackQueryProviderContext}>
          {MAINTENANCE_MODE ? (
            <MaintenancePage />
          ) : (
            <>
              <RouterProvider router={router} />
              <AppUpdateNotifier />
              <PwaShell />
            </>
          )}
        </TanStackQueryProvider.Provider>
      </ErrorBoundary>
    </StrictMode>,
  )
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
